import React, { useState } from 'react';
import { Search, Filter, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';

interface DiscoverHeaderProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedCategory: string | null;
  onCategoryChange: (category: string | null) => void;
  selectedDietTags: string[];
  onDietTagsChange: (tags: string[]) => void;
  onCreateCustomRecipe?: () => void;
  showBreadcrumb?: boolean;
  stickyOffset?: string;
  userTasteProfile?: any;
}

const categories = [
  'All', 'Breakfast', 'Lunch', 'Dinner', 'Snacks', 'Desserts', 'Beverages'
];

const dietTags = [
  'Vegetarian', 'Vegan', 'Gluten-Free', 'Keto', 'Paleo', 'Dairy-Free', 'Low-Carb', 'High-Protein'
];

export function DiscoverHeader({
  searchQuery,
  onSearchChange,
  selectedCategory,
  onCategoryChange,
  selectedDietTags,
  onDietTagsChange,
  onCreateCustomRecipe,
  showBreadcrumb = true,
  stickyOffset = "top-16 md:top-12",
  userTasteProfile
}: DiscoverHeaderProps) {
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const handleCategorySelect = (category: string) => {
    onCategoryChange(category === 'All' ? null : category);
  };

  const handleDietTagToggle = (tag: string) => {
    const newTags = selectedDietTags.includes(tag)
      ? selectedDietTags.filter(t => t !== tag)
      : [...selectedDietTags, tag];
    onDietTagsChange(newTags);
  };

  const clearAllFilters = () => {
    onCategoryChange(null);
    onDietTagsChange([]);
    onSearchChange('');
  };

  const hasActiveFilters = selectedCategory || selectedDietTags.length > 0 || searchQuery;

  return (
    <div className={`bg-white border-b border-stone sticky z-20 ${stickyOffset}`}>
      <div className="max-w-7xl mx-auto space-y-4 px-4 py-5 md:p-6">
        {/* Breadcrumb Navigation */}
        {showBreadcrumb && (
          <nav className="hidden md:block text-sm text-supporting">
            <span className="desktop-hover:text-dusk cursor-pointer">Home</span>
            <span className="mx-2">›</span>
            <span className="text-dusk font-medium">Discover Recipes</span>
          </nav>
        )}
        
        {/* Header Row */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-dusk font-header">Discover Recipes</h1>
            <p className="text-supporting mt-1">{userTasteProfile ? "Recipes personalized for you" : "Find your next favorite meal from thousands of recipes"}</p>
          </div>
          {onCreateCustomRecipe && (
            <Button 
              onClick={onCreateCustomRecipe}
              className="bg-coral desktop-hover:bg-coral/90 text-ink font-medium hidden md:flex"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Recipe
            </Button>
          )}
        </div>
        
        {/* Search and Quick Filters Row */}
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search Bar */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-supporting w-4 h-4" />
            <Input
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search recipes, ingredients, cuisines..."
              className="pl-10 h-12 text-base border-stone focus:ring-warning focus:border-warning"
            />
          </div>
          
          {/* Quick Filter Toggle */}
          <div className="flex items-center gap-2">
            <Button
              variant={showAdvancedFilters ? "default" : "outline"}
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="h-12 px-4"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {hasActiveFilters && (
                <Badge variant="secondary" className="ml-2 h-5 min-w-5 text-xs">
                  {(selectedCategory ? 1 : 0) + selectedDietTags.length}
                </Badge>
              )}
            </Button>
            
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllFilters}
                className="h-12 text-supporting desktop-hover:text-dusk"
              >
                Clear all
              </Button>
            )}
          </div>
        </div>

        {/* Advanced Filters */}
        {showAdvancedFilters && (
          <div className="space-y-4 pt-2 border-t border-stone/20">
            {/* Category Pills */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm font-medium text-dusk">Categories</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <Button
                    key={category}
                    variant={selectedCategory === category || (category === 'All' && !selectedCategory) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleCategorySelect(category)}
                    className="h-8 text-sm transition-all duration-200"
                  >
                    {category}
                  </Button>
                ))}
              </div>
            </div>

            {/* Diet Tags */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm font-medium text-dusk">Dietary Preferences</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {dietTags.map((tag) => (
                  <Badge
                    key={tag}
                    variant={selectedDietTags.includes(tag) ? 'default' : 'secondary'}
                    className="cursor-pointer h-8 text-sm transition-all duration-200 desktop-hover:bg-coral/20"
                    onClick={() => handleDietTagToggle(tag)}
                  >
                    {tag}
                    {selectedDietTags.includes(tag) && (
                      <div className="ml-1 w-3 h-3 bg-current rounded-full opacity-60" />
                    )}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Active Filters Summary */}
        {hasActiveFilters && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-supporting">Active filters:</span>
            <div className="flex flex-wrap gap-1">
              {selectedCategory && (
                <Badge variant="outline" className="text-xs">
                  {selectedCategory}
                </Badge>
              )}
              {selectedDietTags.map((tag) => (
                <Badge key={tag} variant="outline" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}